import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { Col, Row } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import FullProduct from '../../../models/product/FullProduct';
import FullProductCollectionItem from '../../../models/product/FullProductCollectionItem';
import FullProductMatrix from '../../../models/product/FullProductMatrix';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import RequestState from '../../../types/RequestState';
import ProductCollectionMatrixTooltip from '../ProductCollectionMatrixTooltip';
import ProductIncomingQuantity from '../ProductIncomingQuantity';
import ProductMatrixCell from '../ProductMatrixCell';

const messages = defineMessages({
  quantityInput: {
    id: 'product.quantityInput',
    defaultMessage: 'Quantity',
  },
  notAvailable: {
    id: 'product.notAvailable',
    defaultMessage: '(Not available)',
  },
});

const ProductCollectionMatrixQuantityCell = ({
  productCollectionElementItem,
  product,
  productMatrix,
  uiStore,
  element,
  isTooltipOpen,
  onQuantityChange,
  configStore,
  intl,
  isLeftAligned,
}) => {
  const incomingQuantities =
    productCollectionElementItem.getIncomingQuantities();
  const freeQuantity = productCollectionElementItem.product.free_quantity;

  // Create a product object that ProductMatrixCell expects
  const matrixCellProduct = {
    ...productCollectionElementItem.product,
    // Ensure ProductMatrixCell gets the methods it needs
    getFreeQuantity: () => freeQuantity,
    canBeOrderedOutOfStock:
      productCollectionElementItem.canBeOrderedOutOfStock(),
    available_online:
      freeQuantity > 0 || productCollectionElementItem.canBeOrderedOutOfStock(),
    // Add the missing method that ProductMatrixCell expects
    countQuantityWithPackageSizeDecimals: (maxQuantity) => {
      // For collection matrix, we can use the same logic as the main product
      return product.countQuantityWithPackageSizeDecimals
        ? product.countQuantityWithPackageSizeDecimals(maxQuantity)
        : maxQuantity;
    },
    // Ensure other product methods are available
    sellInPackage: product.sellInPackage || false,
    package_size: product.package_size || 1,
    // Only show placeholder text in first column
    package_unit_name: intl.formatMessage(messages.quantityInput),
    stock_unit: intl.formatMessage(messages.quantityInput),
    availability_type: product.availability_type,
  };

  const handleProductQuantity = (cartProduct) => {
    // Convert the ProductMatrixCell format back to our format
    const syntheticEvent = {
      target: {
        name: `qty--${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}`,
        value: cartProduct.qty,
      },
    };
    onQuantityChange(syntheticEvent);
  };

  const checkProductAvailability = () => {
    const quantity = productCollectionElementItem.getAvailableAmount() > 0;
    const ifCanBeOrderedOutOfStock =
      productCollectionElementItem.canBeOrderedOutOfStock();
    const allowBackOrder = product.availability_type === 'ALLOW_BACKORDER';
    return quantity || ifCanBeOrderedOutOfStock || allowBackOrder;
  };

  const ifProductAvailable = checkProductAvailability();

  const renderMobileStorages = () => {
    if (!configStore.productPage.matrixDetailedStockInfoEnabled) {
      return null;
    }

    const isLoadingStorages =
      productMatrix.stocksInfoState === RequestState.LOADING;

    if (isLoadingStorages) {
      return (
        <div className="ProductCollectionMatrixQuantityCell__mobile-storages">
          {[0, 1].map((index) => (
            <div
              key={`skeleton-${index}`}
              className="ProductCollectionMatrixQuantityCell__mobile-storage-item"
            >
              <span className="ProductCollectionMatrixQuantityCell__mobile-storage">
                <Skeleton width="60%" height={18} />
                <Skeleton width="40%" height={14} style={{ marginTop: 4 }} />
              </span>
              <div className="ProductCollectionMatrixQuantityCell__mobile-storage-quantities">
                <Skeleton width={60} height={20} style={{ marginTop: 4 }} />
              </div>
            </div>
          ))}
        </div>
      );
    }

    const stockData = productCollectionElementItem.stocks.get(
      productCollectionElementItem.product.id
    );

    if (!stockData || !stockData.storages || stockData.storages.size === 0) {
      return null;
    }

    return (
      <div className="ProductCollectionMatrixQuantityCell__mobile-storages">
        {Array.from(stockData.storages.values()).map((storage, idx) => {
          const deliveryDays = storage.in_stock_processing_days
            ? storage.in_stock_processing_days.min ===
              storage.in_stock_processing_days.max
              ? `${Math.round(
                  storage.in_stock_processing_days.min
                )} ${intl.formatMessage(globalTranslations.abbrDayUnit)}`
              : `${Math.round(
                  storage.in_stock_processing_days.min
                )}-${Math.round(
                  storage.in_stock_processing_days.max
                )} ${intl.formatMessage(globalTranslations.abbrDayUnit)}`
            : null;

          return (
            <div
              key={idx}
              className="ProductCollectionMatrixQuantityCell__mobile-storage-item"
            >
              <span className="ProductCollectionMatrixQuantityCell__mobile-storage">
                <span className="ProductCollectionMatrixQuantityCell__mobile-storage-name">
                  {storage.free_quantity} {stockData.stock_unit}{' '}
                  {storage.translations.storage_name}
                </span>
                {deliveryDays && (
                  <span className="ProductCollectionMatrixQuantityCell__mobile-storage-delivery-days">
                    ({deliveryDays})
                  </span>
                )}
              </span>
              {storage.incoming_quantities &&
                storage.incoming_quantities.length > 0 && (
                  <div className="ProductCollectionMatrixQuantityCell__mobile-storage-quantities">
                    {storage.incoming_quantities.map(
                      (incomingQuantity, incomingIdx) => (
                        <ProductIncomingQuantity
                          key={incomingIdx}
                          incomingQuantity={incomingQuantity}
                          stockUnit={stockData.stock_unit}
                          className="ProductCollectionMatrixQuantityCell__mobile-storage-incoming"
                        />
                      )
                    )}
                  </div>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleExceptionalAvailabilityClick = () => {
    if (!configStore.productPage.matrixDetailedStockInfoEnabled) {
      const inputElement = document.getElementById(
        `ProductInfo-${productCollectionElementItem.product.id}`
      );
      if (inputElement) {
        inputElement.focus();
      }
    }
  };

  const renderTooltip = (isOpen) => {
    if (configStore.productPage.matrixDetailedStockInfoEnabled) {
      return null;
    }

    const targetId = `qty--${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}`;

    return (
      <ProductCollectionMatrixTooltip
        productCollectionElementItem={productCollectionElementItem}
        isOpen={isOpen || isTooltipOpen}
        productMatrix={productMatrix}
        stockUnit={product.stock_unit}
        targetId={targetId}
      />
    );
  };

  return (
    <td
      key={`${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}`}
      className="ProductCollectionMatrixQuantityCell"
    >
      {uiStore.isMobile && element && (
        <div className="ProductCollectionMatrixQuantityCell__mobile-header">
          <div
            className="ProductCollectionMatrixQuantityCell__mobile-label"
            dangerouslySetInnerHTML={{ __html: element.name }}
          />
          {!configStore.productPage.matrixDetailedStockInfoEnabled && (
            <div className="ProductCollectionMatrixQuantityCell__mobile-stock-info">
              <span className="ProductCollectionMatrixQuantityCell__mobile-stock">
                {freeQuantity} {product.stock_unit}
              </span>
              {(incomingQuantities.length > 0 || !ifProductAvailable) && (
                <div
                  className="ProductCollectionMatrixQuantityCell__exceptionalAvailability"
                  onClick={handleExceptionalAvailabilityClick}
                >
                  {ifProductAvailable ? (
                    <FormattedMessage
                      {...globalTranslations.exceptionalAvailability}
                    />
                  ) : (
                    <FormattedMessage {...messages.notAvailable} />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {uiStore.isMobile && renderMobileStorages()}
      <Row className="ProductCollectionMatrixQuantityCell__quantity-selection-row">
        {!configStore.productPage.matrixDetailedStockInfoEnabled &&
          !uiStore.isMobile && (
            <>
              <Col xs={12}>
                <div
                  className={classNames(
                    'ProductCollectionMatrixQuantityCell__quantity-and-stock-unit',
                    {
                      'ProductCollectionMatrixQuantityCell__quantity-and-stock-unit--left-aligned':
                        isLeftAligned,
                    }
                  )}
                  htmlFor={`qty--${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}`}
                >
                  {freeQuantity} {product.stock_unit}
                </div>
              </Col>
              {(incomingQuantities.length > 0 || !ifProductAvailable) && (
                <Col
                  xs={12}
                  className={classNames(
                    'ProductCollectionMatrixQuantityCell__exceptionalAvailability-container',
                    {
                      'ProductCollectionMatrixQuantityCell__exceptionalAvailability-container--left-aligned':
                        isLeftAligned,
                    }
                  )}
                >
                  <div
                    className="ProductCollectionMatrixQuantityCell__exceptionalAvailability"
                    onClick={handleExceptionalAvailabilityClick}
                  >
                    {ifProductAvailable ? (
                      <FormattedMessage
                        {...globalTranslations.exceptionalAvailability}
                      />
                    ) : (
                      <FormattedMessage {...messages.notAvailable} />
                    )}
                  </div>
                </Col>
              )}
            </>
          )}
        <Col
          xs={12}
          className={classNames(
            'ProductCollectionMatrixQuantityCell__quantity-selection-unit',
            {
              'ProductCollectionMatrixQuantityCell__quantity-selection-unit--left-aligned':
                isLeftAligned,
            }
          )}
        >
          <div
            id={`qty--${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}`}
          >
            <ProductMatrixCell
              product={matrixCellProduct}
              cellCount={1}
              onHandleProductQuantity={handleProductQuantity}
              onRenderTooltip={renderTooltip}
              className="ProductCollectionMatrixQuantityCell__matrix-cell"
              propertyListRef={null}
            />
          </div>
        </Col>
      </Row>
    </td>
  );
};

ProductCollectionMatrixQuantityCell.propTypes = {
  productCollectionElementItem: modelOf(FullProductCollectionItem).isRequired,
  product: modelOf(FullProduct).isRequired,
  productMatrix: modelOf(FullProductMatrix).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  intl: intlShape.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  element: PropTypes.object,
  isTooltipOpen: PropTypes.bool,
  isLeftAligned: PropTypes.bool,
};

export default injectIntl(
  inject(
    'uiStore',
    'configStore'
  )(observer(ProductCollectionMatrixQuantityCell))
);
